import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'

/**
 * The "Sustainability as a Strategic Advantage" e-book's own page.
 *
 * Built to the design of the digitopia.co page it replaces
 * (`/impact-labs/ebooks/sustainability-as-a-strategic-advantage/`), sent as five
 * screenshots on 2026-08-28 with *"referans alarak ilgili sayfayı tastamam
 * şekilde, aynısını, tıpkısını tasarla"*. Band for band that page's: the
 * two-column hero with the glass-building-and-tree artwork beside it, the grey
 * "What's Inside" card with the pair of cover mockups and the five bullets, the
 * "Why Download" card with the stacked copies and three bullets, and the centred
 * "Don't Miss Out! Download Your Free Ebook Now" ask. The copy is that page's
 * copy, verbatim.
 *
 * It follows its closest sibling, `ebook-sustainability-innovation.ts`, in every
 * place the reference and this site disagree — the same departures, for the same
 * reasons:
 *
 *  * **The eyebrow is the site's own**, over the copy column, rather than
 *    digitopia.co's small left-pinned "UNLOCKING HIDDEN BUSINESS VALUE" label.
 *    Every other hero here is that shape, and the words are unchanged.
 *  * **The download is a direct link to the PDF, not a gated form.** The
 *    reference puts a nine-field HubSpot form between the reader and the file;
 *    no form on this site submits anywhere yet, so reproducing it would mean the
 *    page's only call to action silently drops every lead. The `check` below
 *    says so, as the sibling pages' do.
 *  * **A testimonial closes the page**, which the reference does not have — it
 *    is what the sibling e-book pages do. The card is CFN Kimya's: of the twenty
 *    in `content/testimonials.ts` it is the one about **sustainability** maturity
 *    rather than digital, which is this book's subject. Its words are the Hall of
 *    Fame's; that file is where a change to them belongs.
 *
 * **The reference's newsletter block is a Contact Us ask here**, asked for in the
 * same message (*"aşağıdaki subscribe kısmı yerine contact us sayfası
 * yeterli"*). digitopia.co closes that band with a "Join Our Community" heading
 * and a Subscribe button; there is no newsletter subscription on this site and no
 * form that submits, so the band keeps its place and its argument and sends the
 * reader to `/en/contact-us` instead. Its copy no longer promises a newsletter,
 * because a button labelled Contact Us under a sentence about subscribing is a
 * broken promise rather than a design detail.
 *
 * **The slug needed no correction.** `content/ebooks.ts` derived
 * `sustainability-as-a-strategic-advantage` from the title and the screenshots
 * came from exactly that address — the first e-book of the run where the title
 * rule was already right. The catalogue entry now points its card here.
 */
export const route: RouteDef = {
  id: 'ebookSustainabilityAsAStrategicAdvantage',
  path: 'impact-labs/ebooks/sustainability-as-a-strategic-advantage',
  priority: 0.7,
}

/**
 * The PDF, in the `website-documents` bucket — the same object the e-book
 * catalogue already points its card at. It is a local constant rather than a key
 * in `content/pages/links.ts`, because a page's own download is nobody else's
 * and five concurrent runs appending to that file is a push conflict.
 */
const EBOOK_PDF
  = 'https://njdxtkezxgkrmwwhzcne.supabase.co/storage/v1/object/public/website-documents/ebook/'
  + '3877d853-06ed-4241-9356-54d8e3af9635-Sustainability%20as%20a%20Strategic%20Advantage.pdf'

export const ebookSustainabilityAsAStrategicAdvantage: WrittenPage = {
  navLabel: 'Sustainability as a Strategic Advantage',
  title: 'Sustainability as a Strategic Advantage',
  description:
    'Unlocking hidden business value: how integrating sustainable practices drives growth, '
    + 'innovation and long-term success. Download the e-book from Digitopia Impact Labs.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Unlocking Hidden Business Value',
      headline: 'Sustainability as a Strategic Advantage',
      body: [
        'In today’s competitive landscape, sustainability is no longer a buzzword. It is a '
          + 'strategic imperative. Our latest ebook reveals how integrating sustainable practices can drive '
          + 'growth, innovation, and long-term success for your business.',
      ],
      ctas: [{ label: 'Download', href: EBOOK_PDF }],
      photo: {
        src: '/photo/sustainability-strategic-advantage-ebook-artwork.webp',
        alt:
          'The e-book’s cover artwork: a glass office building with a large green tree standing '
          + 'in front of it, reflected in the façade.',
      },
      // The artwork is square and the site's hero box is 3:2, which would take
      // the sky and the paving off it.
      photoFrame: 'square',
      /*
        Clause 1 — THE DOWNLOAD IS NOT GATED — resolved 2026-08-30. It said no
        form on this site submits anywhere and the Send button is disabled on
        purpose. Both stopped being true today: the form posts to
        `website-form-lead`, the submission opens a task on its page's own
        counterpart, and every Download here carries `data-gated-download`, so
        the reader is asked for an email and the PDF is sent to it. No HubSpot
        form id is needed for any of it. What is left is clause 2.
      */
      check:
        'THE ARTWORK IS 768–900px WIDE. Those are the largest copies of the '
        + 'cover artwork and the two mockups anybody has sent; every other hero on '
        + 'the site is 2560px, so this one is painted at roughly its own width on a '
        + 'laptop with nothing left over for a retina screen. It is generated art '
        + 'rather than a photograph, so a bigger export should exist wherever it was '
        + 'made:\n'
        + '     node apps/website/scripts/attach.mjs <file> photo-src/sustainability-strategic-advantage-ebook-artwork.webp\n'
        + '     pnpm --filter @digitopia/website images\n'
        + '   and nothing in this file changes.',
    },
    {
      kind: 'cta',
      tone: 'card',
      // Mockup left, offer right — the reference's order, and it alternates with
      // the card under it.
      flip: true,
      heading: 'What’s Inside',
      points: [
        'Insights on the evolving business landscape and the imperative of sustainability.',
        'Strategies for leveraging sustainability to drive growth and innovation.',
        'Quantifiable financial, operational, and reputational benefits of sustainable practices.',
        'Real-world case studies from industry leaders like Patagonia and Unilever.',
        'Practical steps for initiating and measuring sustainability programs.',
      ],
      ctas: [{ label: 'Download the Report', href: EBOOK_PDF }],
      photo: {
        src: '/photo/sustainability-strategic-advantage-ebook-covers.webp',
        alt:
          'Two copies of Sustainability as a Strategic Advantage, one behind the other, on a pale '
          + 'gradient.',
      },
    },
    {
      kind: 'cta',
      tone: 'card',
      heading: 'Why Download',
      points: [
        'Learn how to turn sustainability from a compliance task into a strategic advantage.',
        'Discover actionable strategies to enhance your business’s resilience and '
          + 'competitiveness.',
        'Get inspired by real-world examples of successful sustainability integration.',
      ],
      ctas: [{ label: 'Download the Report', href: EBOOK_PDF }],
      photo: {
        src: '/photo/sustainability-strategic-advantage-ebook-stack.webp',
        alt:
          'A stack of printed copies of Sustainability as a Strategic Advantage seen from above.',
      },
    },
    {
      kind: 'prose',
      heading: 'Join Our Community',
      // The reference's newsletter block, without the newsletter: Contact Us
      // rather than Subscribe, asked for on 2026-08-28.
      paragraphs: [
        'Sustainability moves quickly, and what it means for your business is rarely what it '
        + 'means for the one next door. Talk to us about where your organization stands and what '
        + 'the next step is worth.',
      ],
      cta: { label: 'Contact Us', href: '#contact' },
    },
    {
      kind: 'prose',
      align: 'center',
      heading: 'Don’t Miss Out! Download Your Free Ebook Now',
      sub:
        'Transform your business with sustainability. Get your copy of “Sustainability as a '
        + 'Strategic Advantage: Unlocking Hidden Business Value” and start your journey towards a '
        + 'more prosperous and sustainable future.',
      cta: { label: 'Download the Ebook', href: EBOOK_PDF },
    },
    {
      kind: 'quotes',
      variant: 'fame',
      heading: 'What our customers say.',
      items: [
        {
          // The Hall of Fame's CFN Kimya card, word for word — `content/testimonials.ts`
          // is where these words are edited, and the Turkish page carries its own
          // saying of them.
          quote:
            'Digital transformation and sustainability are two major business imperatives of '
            + 'modern business world. In that respect, assessing where we are and where we would '
            + 'like to be and having an aligned understanding and commitment among our management '
            + 'team is very precious. We chose Digitopia because of their professionalism and '
            + 'sound methodology in maturity models of both digital and sustainability '
            + 'transformation.',
          source: 'Fatma Köktaş, Board Member and CEO',
          company: 'CFN Kimya',
          logo: { name: 'CFN Kimya', src: '/logo/cfnkimya.webp' },
          portrait: { src: '/photo/portrait-fatma-koktas.png', alt: 'Fatma Köktaş' },
        },
      ],
    },
  ],
}
